import { uiSurface } from "shared/ui/uiSurface"
import { EditOutlined, PowerSettingsNew } from "@mui/icons-material"
import { Avatar, Chip, IconButton, Stack, Typography } from "@mui/material"
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid"
import dayjs from "dayjs"
import { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CourierItem } from "../../apis/appApi"
import { getAvatarColor } from "../customers/utils"

type Props = {
  rows: CourierItem[]
  loading: boolean
  rowCount: number
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
  onRowClick: (id: number) => void
  onEdit: (courier: CourierItem) => void
  onToggle: (courier: CourierItem) => void
}

export const CouriersGrid: FC<Props> = ({
  rows,
  loading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  onRowClick,
  onEdit,
  onToggle,
}) => {
  const { t } = useTranslation()

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t("couriers.col.name"),
        flex: 1.5,
        minWidth: 200,
        valueGetter: (_value, row) => `${row.firstName} ${row.lastName}`,
        renderCell: (params) => {
          const row = params.row as CourierItem
          const fullName = `${row.firstName} ${row.lastName}`
          const initial = row.firstName?.charAt(0)?.toUpperCase() || "?"
          const color = getAvatarColor(fullName)
          return (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: "100%" }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  fontWeight: 600,
                  bgcolor: color,
                }}
              >
                {initial}
              </Avatar>
              <Typography variant="body2" fontWeight={600} noWrap>
                {fullName}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: "username",
        headerName: t("couriers.col.username"),
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => (
          <Typography variant="body2" noWrap>
            {params.row.username}
          </Typography>
        ),
      },
      {
        field: "phoneNumber",
        headerName: t("couriers.col.phone"),
        flex: 0.6,
        minWidth: 100,
        renderCell: (params) => (
          <Typography variant="body2" color={params.row.phoneNumber ? undefined : "text.disabled"} noWrap>
            {params.row.phoneNumber || "\u2014"}
          </Typography>
        ),
      },
      {
        field: "enabled",
        headerName: t("couriers.col.status"),
        flex: 0.5,
        minWidth: 100,
        renderCell: (params) => {
          const enabled = params.row.enabled
          return (
            <Stack justifyContent="center" sx={{ height: "100%" }}>
              <Chip
                label={enabled ? t("couriers.status.active") : t("couriers.status.disabled")}
                size="small"
                sx={
                  enabled
                    ? { bgcolor: "#e8f5e9", color: "#2e7d32" }
                    : { bgcolor: "#ffebee", color: "#c62828" }
                }
              />
            </Stack>
          )
        },
      },
      {
        field: "roles",
        headerName: t("couriers.col.roles"),
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const roles = (params.row.roles as CourierItem["roles"]) ?? []
          if (roles.length === 0) return null
          const ROLE_LABELS: Record<string, { label: string; sx?: object }> = {
            ROLE_HAULER_MANAGER: { label: t("couriers.role.manager"), sx: { bgcolor: "#e3f2fd", color: "#1565c0" } },
            ROLE_HAULER_EMPLOYEE: { label: t("couriers.role.employee") },
            ROLE_USER: { label: "User" },
          }
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%", flexWrap: "wrap", gap: 0.5 }}>
              {roles.map((r) => {
                const cfg = ROLE_LABELS[r.authority] ?? { label: r.authority.replace("ROLE_", "").replace(/_/g, " ") }
                return (
                  <Chip key={r.id} label={cfg.label} size="small" sx={cfg.sx} />
                )
              })}
            </Stack>
          )
        },
      },
      {
        field: "lastLogin",
        headerName: t("couriers.col.lastLogin"),
        flex: 0.6,
        minWidth: 120,
        renderCell: (params) => {
          const value = params.row.lastLogin
          return (
            <Typography variant="body2" color={value ? undefined : "text.disabled"} noWrap>
              {value ? dayjs(value).format("MMM D, YYYY") : t("couriers.lastLogin.never")}
            </Typography>
          )
        },
      },
      {
        field: "actions",
        headerName: t("couriers.col.actions"),
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const row = params.row as CourierItem
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
              <IconButton
                size="small"
                data-testid={`couriers-edit-btn-${row.id}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(row)
                }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                data-testid={`couriers-toggle-btn-${row.id}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle(row)
                }}
                sx={{ color: row.enabled ? "#c62828" : "#2e7d32" }}
              >
                <PowerSettingsNew fontSize="small" />
              </IconButton>
            </Stack>
          )
        },
      },
    ],
    [t, onEdit, onToggle],
  )

  const handleRowClick = useCallback(
    (params: any) => {
      onRowClick(params.id as number)
    },
    [onRowClick],
  )

  return (
    <DataGrid
      {...uiSurface("UI-DLV2-121")}
      data-testid="couriers-grid"
      rows={rows}
      columns={columns}
      loading={loading}
      rowCount={rowCount}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      pageSizeOptions={[10, 25, 50]}
      onRowClick={handleRowClick}
      disableRowSelectionOnClick
      sx={{
        border: 0,
        height: "100%",
        "& .MuiDataGrid-row": { cursor: "pointer" },
      }}
    />
  )
}
